import getStroke from 'perfect-freehand';

import type { DoodleStroke } from './use-doodle';

const STROKE_OPTIONS_BASE = {
	thinning: 0.6,
	smoothing: 0.5,
	streamline: 0.5,
	easing: (t: number) => Math.sin((t * Math.PI) / 2),
	last: true,
};

const BRUSH_SIZE_SCALE = 3;
const ERASER_SIZE_SCALE = 4.25;

function average(a: number, b: number): number {
	return (a + b) / 2;
}

export function getSvgPathFromStroke(points: number[][]): string {
	const len = points.length;
	if (len < 4) return '';

	let a = points[0];
	let b = points[1];
	const c = points[2];

	let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(2)},${b[1].toFixed(2)} ${average(b[0], c[0]).toFixed(2)},${average(b[1], c[1]).toFixed(2)} T`;

	for (let i = 2, max = len - 1; i < max; i++) {
		a = points[i];
		b = points[i + 1];
		result += `${average(a[0], b[0]).toFixed(2)},${average(a[1], b[1]).toFixed(2)} `;
	}

	result += 'Z';
	return result;
}

export function getStrokeOutline(stroke: DoodleStroke): number[][] {
	const simulatePressure = !stroke.points.some((p) => p.length >= 3 && p[2] !== 0.5);

	return getStroke(stroke.points, {
		...STROKE_OPTIONS_BASE,
		size: stroke.size * (stroke.tool === 'eraser' ? ERASER_SIZE_SCALE : BRUSH_SIZE_SCALE),
		simulatePressure,
	});
}

export function renderStrokeToCtx(ctx: CanvasRenderingContext2D, stroke: DoodleStroke): void {
	const outline = getStrokeOutline(stroke);
	const pathData = getSvgPathFromStroke(outline);
	if (!pathData) return;

	const path = new Path2D(pathData);
	ctx.fillStyle = stroke.tool === 'eraser' ? '#ffffff' : stroke.color;
	ctx.fill(path);
}

/** True when a stroke would paint pixels (not a degenerate tap/flick). */
export function strokeHasRenderedContent(stroke: DoodleStroke): boolean {
	return getSvgPathFromStroke(getStrokeOutline(stroke)) !== '';
}

/** True when at least one committed stroke would paint pixels. */
export function hasRenderedContent(strokes: DoodleStroke[]): boolean {
	return strokes.some(strokeHasRenderedContent);
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error('Failed to export canvas'));
		}, 'image/png');
	});
}
